import React, { useState, useEffect } from 'react';
import { PageHeader, Card, Btn, Badge, Table, Modal } from '../../components/UI';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_URL = 'https://api.msreach.msdiscoveries.com';

export default function Billing() {
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [txnId, setTxnId] = useState('');
  const [billing, setBilling] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Load profile separately so each section loads independently
    api.get('/api/users/profile')
      .then(r => { if (r.data.success) setProfile(r.data.user); })
      .catch(e => console.error('Profile error:', e))
      .finally(() => setProfileLoading(false));

    api.get('/api/billing/invoices')
      .then(r => { if (r.data.success) setInvoices(r.data.invoices || []); })
      .catch(e => console.error('Invoices error:', e))
      .finally(() => setInvoicesLoading(false));
  }, []);

  const submitRenewal = async () => {
    if (!txnId.trim()) { toast.error('Enter transaction ID'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/billing/submit', {
        plan_id: profile?.plan_id, billing_cycle: billing,
        currency: profile?.currency || 'INR',
        transaction_id: txnId, payment_type: 'renewal'
      });
      if (data.success) {
        toast.success('Renewal submitted! Awaiting admin approval.');
        setModal(false); setTxnId('');
        // Reload invoices
        const r = await api.get('/api/billing/invoices');
        if (r.data.success) setInvoices(r.data.invoices || []);
      } else toast.error(data.message);
    } catch(e) { toast.error('Error submitting payment'); }
    finally { setSubmitting(false); }
  };

  const downloadInvoice = (id) => {
    const token = localStorage.getItem('msreach_token');
    window.open(`${API_URL}/api/billing/invoice/${id}/download?token=${token}`, '_blank');
  };

  return (
    <div>
      <PageHeader title="Billing & Invoices" />

      {/* Current Plan */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>📋 Current Plan</div>
        {profileLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
            <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.1)', borderTop: '2px solid var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            Loading plan details...
          </div>
        ) : !profile ? (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
            Unable to load plan. <button onClick={() => window.location.reload()} style={{ color: 'var(--green)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 20, marginBottom: 20 }}>
              {[
                ['PLAN', <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'Sora', color: 'var(--green)' }}>{profile.plan_name || 'No Plan'}</span>],
                ['STATUS', <Badge>{profile.status || 'pending'}</Badge>],
                ['BILLING', <span style={{ color: '#fff', fontWeight: 600 }}>{profile.billing_cycle || '—'}</span>],
                ['EXPIRES', <span style={{ color: profile.plan_expiry_date ? '#ffd700' : 'rgba(255,255,255,0.4)', fontWeight: 600 }}>{profile.plan_expiry_date ? new Date(profile.plan_expiry_date).toLocaleDateString('en-IN') : '—'}</span>],
                ['CURRENCY', <span style={{ color: '#fff', fontWeight: 600 }}>{profile.currency || 'INR'}</span>],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
                  <div>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Btn onClick={() => setModal(true)}>🔄 Renew Plan</Btn>
              <Btn variant="outline" onClick={() => window.location.href = '/plans'}>⬆️ View Plans</Btn>
            </div>
          </>
        )}
      </Card>

      {/* Bank Details */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>🏦 Payment Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { flag: '🇮🇳', title: 'Indian Bank Transfer', rows: [['Name','M/S MS DISCOVERIES'],['Account','759601010050400'],['Bank','Union Bank of India'],['Branch','Paramathi Velur'],['IFSC','UBIN0575968'],['SWIFT','UBININBBBERD']] },
            { flag: '🇺🇸', title: 'US Bank Transfer', rows: [['Name','MS DISCOVERIES'],['Account','8336276214'],['ACH Routing','026073150'],['Bank','Community Federal Savings Bank'],['Address','5 Penn Plaza, New York, NY 10001']] },
          ].map(b => (
            <div key={b.title} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 14 }}>{b.flag} {b.title}</div>
              {b.rows.map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>{k}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 500 }}>{v}</span>
                    <button onClick={() => { navigator.clipboard.writeText(v); toast.success('Copied!'); }} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: 'rgba(255,255,255,0.4)', padding: '2px 8px', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>Copy</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Card>

      {/* Payment History */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📄 Payment History & Invoices</span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontWeight: 400 }}>{invoices.length} record{invoices.length !== 1 ? 's' : ''}</span>
        </div>
        <Table
          loading={invoicesLoading}
          emptyMsg="No payments yet. Submit a payment to see invoices here."
          columns={[
            { key: 'invoice_number', label: 'Invoice #', render: v => <span style={{ fontWeight: 700, color: 'var(--green)', fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</span> },
            { key: 'created_at', label: 'Date', render: v => <span style={{ color: 'rgba(255,255,255,0.7)' }}>{v ? new Date(v).toLocaleDateString('en-IN') : '—'}</span> },
            { key: 'plan_name', label: 'Plan', render: v => <span style={{ color: '#fff' }}>{v || '—'}</span> },
            { key: 'billing_cycle', label: 'Cycle', render: v => <span style={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>{v || '—'}</span> },
            { key: 'subtotal', label: 'Amount', render: (v, r) => <span style={{ color: '#fff' }}>{r.currency === 'USD' ? '$' : '₹'}{parseFloat(v || 0).toFixed(2)}</span> },
            { key: 'gst_type', label: 'GST', render: v => <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{!v || v === 'NONE' ? 'N/A' : v === 'SGST_CGST' ? 'CGST+SGST' : 'IGST'}</span> },
            { key: 'total_amount', label: 'Total', render: (v, r) => <span style={{ fontWeight: 700, color: 'var(--green)' }}>{r.currency === 'USD' ? '$' : '₹'}{parseFloat(v || 0).toFixed(2)}</span> },
            { key: 'status', label: 'Status', render: v => <Badge>{v || 'pending'}</Badge> },
            { key: 'id', label: 'Invoice', render: (v, r) => (
              r.status === 'approved'
                ? <button onClick={() => downloadInvoice(v)} style={{ background: 'rgba(0,231,76,0.1)', color: 'var(--green)', border: '1px solid rgba(0,231,76,0.2)', padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📥 Download</button>
                : <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>{r.status === 'pending' ? '⏳ Pending approval' : '—'}</span>
            )},
          ]}
          data={invoices}
        />
      </div>

      {/* Renew Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="🔄 Renew Plan">
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, marginBottom: 20 }}>Transfer the amount to our bank account and submit your transaction ID below.</p>
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', marginBottom: 8 }}>🏦 Indian Bank — Union Bank of India</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 2 }}>
            M/S MS DISCOVERIES &nbsp;|&nbsp; A/C: 759601010050400<br />
            IFSC: UBIN0575968 &nbsp;|&nbsp; Branch: Paramathi Velur
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Billing Cycle</label>
          <select value={billing} onChange={e => setBilling(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 13 }}>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly (Save 20%)</option>
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Transaction ID / Reference Number *</label>
          <input value={txnId} onChange={e => setTxnId(e.target.value)} placeholder="Enter your bank transaction ID" style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '11px 14px', color: '#fff', fontSize: 14 }} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Btn onClick={submitRenewal} disabled={submitting} full>{submitting ? 'Submitting...' : 'Submit Payment'}</Btn>
          <Btn variant="outline" onClick={() => setModal(false)} full>Cancel</Btn>
        </div>
      </Modal>
    </div>
  );
}